import React, { useState, useRef, useCallback } from 'react';
import { filenameFromResponse, previewAndSaveBlob } from '../utils/download';
import './MergepointDashboard.css';

// Mergepoint: the re-assembly half of the DOCX report workflow.
//
//   1. In the Reporting Portal, generate a report as "DOCX" — this
//      downloads a ZIP pack: one .docx per chapter plus a manifest.json.
//   2. Hand-edit any chapter's .docx (fix page breaks, add images/tables,
//      trim pages). Keep every chapter — don't add or remove whole ones —
//      and keep manifest.json inside the archive.
//   3. Zip it back up and drop it here. Mergepoint renders each chapter,
//      counts pages, rebuilds the Table of Contents with correct page
//      numbers, merges everything, and stamps "Page x of n".
//
// The download lives on the object's report screen; Mergepoint only does
// the assembly.

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const STEPS = [
  {
    n: 1,
    title: 'Export as DOCX',
    body: 'In the Reporting Portal, open the object and generate its report as "DOCX". You get a ZIP pack, consisting of one document per chapter, plus manifest.json.',
  },
  {
    n: 2,
    title: 'Edit the chapters',
    body: 'Unzip, fix pagination or add content in any chapter’s .docx, then zip it back up. Keep every chapter and keep manifest.json inside. Ensure to not add or remove whole chapters.',
  },
  {
    n: 3,
    title: 'Assemble here',
    body: 'Drop the edited ZIP below. Mergepoint recounts pages, rebuilds the Table of Contents with real page numbers, merges, and stamps every page.',
  },
];

export default function MergepointDashboard() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const pickFile = useCallback((f) => {
    setError('');
    if (!f) return;
    const isZip = /\.zip$/i.test(f.name) ||
      f.type === 'application/zip' ||
      f.type === 'application/x-zip-compressed';
    if (!isZip) {
      setError('Please choose a .zip pack (the DOCX export).');
      return;
    }
    setFile(f);
  }, []);

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const f = e.dataTransfer?.files?.[0];
    pickFile(f);
  }

  async function handleAssemble() {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await fetch(`${API_BASE}/reports/assemble`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      // Open the assembled PDF in a new tab (the success signal) and also
      // force a correctly-named download — a blob tab otherwise saves under
      // a random UUID and can even fail to download once the URL is revoked.
      previewAndSaveBlob(blob, filenameFromResponse(res, 'assembled-report.pdf'));
    } catch (err) {
      setError(err?.message || 'Assembly failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mp-page">
      <div className="mp-header">
        <h1 className="mp-title">Mergepoint</h1>
        <p className="mp-subtitle">
          Turn hand-edited report chapters back into one page-numbered PDF,
          with a rebuilt Table of Contents.
        </p>
      </div>

      <div className="mp-steps">
        {STEPS.map((s) => (
          <div key={s.n} className="mp-step">
            <div className="mp-step-num">{s.n}</div>
            <div className="mp-step-text">
              <h3 className="mp-step-title">{s.title}</h3>
              <p className="mp-step-body">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mp-assemble">
        <h2 className="mp-section-title">Assemble final report</h2>

        <div
          className={`mp-drop${dragOver ? ' drag' : ''}${file ? ' has-file' : ''}`}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) inputRef.current?.click(); }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            style={{ display: 'none' }}
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <svg className="mp-drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {file ? (
            <div className="mp-drop-file">
              <span className="mp-drop-file-name">{file.name}</span>
              <button
                type="button"
                className="mp-drop-clear"
                onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="mp-drop-prompt">
              <span className="mp-drop-lead">Drop the edited ZIP here</span>
              <span className="mp-drop-sub">or click to browse</span>
            </div>
          )}
        </div>

        {error && <div className="mp-error">{error}</div>}

        <div className="mp-actions">
          <button
            className="btn btn-primary mp-assemble-btn"
            onClick={handleAssemble}
            disabled={!file || busy}
          >
            {busy ? 'Assembling…' : 'Assemble final PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
